import { useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { observer } from 'mobx-react-lite';

import { IconArrowLeft } from '@components/icons/arrow-left';
import { IconBrandADManager } from '@components/icons/brand-ad-manager';
import { IconBrandH3 } from '@components/icons/brand-h3';
import { IconBrandHireflix } from '@components/icons/brand-hireflix';
import { IconBrandJourneys } from '@components/icons/brand-journeys';
import { IconEdit } from '@components/icons/edit';
import { IconHire } from '@components/icons/hire';
import { IconMessages } from '@components/icons/messages';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOnboarding } from '@components/icons/onboarding';
import { IconReject } from '@components/icons/reject';
import { IconSort } from '@components/icons/sort';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';

import { useFeature } from 'features/authentication/authentication-queries';
import { useModal } from 'features/modals';
import { usePermissions } from 'features/permissions';
import { useManageJobAccess } from 'hooks/use-manage-job-access';
import { useADManagerEnabled } from 'integrations/adManager/hooks';
import { useBulkCreateH3CoreSidebar } from 'integrations/H3core/bulk-create-sidebar';
import { useBulkEditH3CoreSidebar } from 'integrations/H3core/bulk-edit-sidebar';
import { useIsH3CoreEnabled } from 'integrations/H3core/hooks';
import { useHireflixEnabled } from 'integrations/hireflix/hooks';
import { useAddToJourneySidebar } from 'integrations/journeys/add-to-journey-sidebar';
import {
  useHasJourneysAutomation,
  useIsAppsAdmin,
  useIsJourneysEnabled,
} from 'integrations/journeys/hooks';
import { useJobTriggers } from 'integrations/journeys/job-triggers/hooks';
import {
  jobTriggerCountQueryKey,
  jobTriggersQueryKey,
} from 'integrations/journeys/job-triggers/query-keys';
import { useAddStageTrigger } from 'integrations/journeys/job-triggers/use-add-stage-trigger';
import { useIsOnboardingFormEnabled } from 'integrations/onboardingForm/api/hooks';
import { useStore } from 'models/Provider';

export const StageActionMenu = observer(({ onSelect, stage, onEdit }) => {
  const [page, setPage] = useState(0);
  const { showModal } = useModal();
  const { hasRolePermission } = usePermissions();
  const showH3BulkEditSidebar = useBulkEditH3CoreSidebar();
  const showH3BulkCreateSidebar = useBulkCreateH3CoreSidebar();
  const showJourneysCreateSidebar = useAddToJourneySidebar();
  const queryClient = useQueryClient();

  function selectMenuEntry(entry) {
    onSelect(entry);
    setPage(0);
  }

  async function onRemove() {
    // JourneyTrigger.job_stage is on_delete=CASCADE, so removing a stage silently
    // removes its trigger. The delete path is MST-only and invalidates nothing, so
    // the header badge, the Journeys tab badge and the banner gate would all keep
    // counting it until a reload.
    // The id is captured before the await: removeStage destroys the MST stage
    // node on success, and any property read of a destroyed node throws.
    const jobId = stage.job.id;
    const wasRemoved = await stage.job.removeStage(stage.id);
    if (wasRemoved) {
      queryClient.invalidateQueries(jobTriggersQueryKey(jobId));
      queryClient.invalidateQueries(jobTriggerCountQueryKey(jobId));
    }
  }

  function click() {
    setPage(0);
  }

  function goToSort() {
    setPage(1);
  }

  function goToRoot() {
    setPage(0);
  }

  async function rejectAllInStage() {
    click();
    await stage.loadAll();
    showModal('REJECT_APPLICANTS', {
      _container: {
        autoWidth: false,
        width: '37.5rem',
      },
      applicants: stage.rejectableApplications,
    });
  }

  async function hireAllInStage() {
    click();
    await stage.loadAll();
    showModal('BULK_HIRE_APPLICANTS', {
      applicants: stage.activeApplications,
    });
  }

  async function messageAllInStage() {
    click();
    await stage.loadAll();
    showModal('MESSAGE_APPLICANTS', {
      _container: {
        width: '37.5rem',
      },
      applicants: stage.applications,
    });
  }

  async function bulkHireflixInvite() {
    click();
    await stage.loadAll();
    const applicants = stage.applications;
    const job = stage.job;
    showModal('INTEGRATION_HIREFLIX_BULK_SEND_INTERVIEW', {
      applicants,
      job,
    });
  }

  async function bulkADManagerCreateUser() {
    click();
    await stage.loadAll();
    const applicants = stage.applications;
    const job = stage.job;
    showModal('INTEGRATION_ADMANAGER_BULK_CREATE_USER', {
      applicationIDs: applicants.map((applicant) => applicant.id),
      jobID: job.id,
    });
  }

  async function bulkSendOnboardingForm() {
    click();
    await stage.loadAll();
    const job = stage.job;
    showModal('INTEGRATION_ONBOARDING_FORM_SEND', {
      applicants: stage.hiredApplications,
      job,
    });
  }

  async function bulkEditH3() {
    click();
    await stage.loadAll();
    const applicants = stage.hiredApplications.filter(
      (application) => !application.integrationsResults || !application.integrationsResults.h3core
    );

    showH3BulkEditSidebar({
      applicants: applicants,
    });
  }

  async function bulkCreateH3() {
    click();
    await stage.loadAll();
    const applicants = stage.hiredApplications.filter(
      (application) => !application.integrationsResults || !application.integrationsResults.h3core
    );

    showH3BulkCreateSidebar({
      applicants: applicants,
    });
  }

  async function bulkCreateJourneys() {
    click();
    await stage.loadAll();
    showJourneysCreateSidebar({
      applications: stage.applications.map((applicant) => applicant.id),
      jobId: stage.job.id,
    });
  }

  const hireflixEnabled =
    useHireflixEnabled(stage.job.id) && hasRolePermission('application:bulk-send-hireflix');
  const onboardingFormEnabled =
    useIsOnboardingFormEnabled() && hasRolePermission('application:bulk-send-onboarding');
  const adManagerEnabled =
    useADManagerEnabled(stage.job.id) && hasRolePermission('application:bulk-send-admanager');
  const h3CoreEnabled = useIsH3CoreEnabled() && hasRolePermission('application:bulk-edit-h3core');
  const hasJourneysIntegrationFlag = useFeature('FEATURE_INTEGRATION_JOURNEYS');
  const canViewJourneys =
    hasJourneysIntegrationFlag && hasRolePermission('application:bulk-create-journeys');
  const isJourneysEnabled = useIsJourneysEnabled();
  const hasJourneysAutomation = useHasJourneysAutomation();

  return (
    <Menu
      buttonProps={{
        iconLeft: <IconMoreVertical />,
        variant: 'ghost',
        dataCy: 'stage-action-menu',
      }}
    >
      {page === 0 && (
        <PageZero
          onReject={rejectAllInStage}
          onHire={hireAllInStage}
          onMessage={messageAllInStage}
          onHireflix={hireflixEnabled && bulkHireflixInvite}
          onADManager={adManagerEnabled && bulkADManagerCreateUser}
          onOnboardingForm={onboardingFormEnabled && bulkSendOnboardingForm}
          onH3CoreEdit={h3CoreEnabled && bulkEditH3}
          onH3CoreCreate={h3CoreEnabled && bulkCreateH3}
          onJourneysCreate={canViewJourneys && bulkCreateJourneys}
          isJourneysEnabled={isJourneysEnabled}
          hasJourneysAutomation={hasJourneysAutomation}
          nextPage={goToSort}
          onEdit={onEdit}
          onRemove={onRemove}
          stage={stage}
        />
      )}
      {page === 1 && (
        <PageOne goBack={goToRoot} onSortClick={selectMenuEntry} currentSort={stage.sort} />
      )}
    </Menu>
  );
});

const PageZero = observer((props) => {
  const {
    nextPage,
    onReject,
    onHire,
    onMessage,
    onEdit,
    onRemove,
    onHireflix,
    onADManager,
    onOnboardingForm,
    onH3CoreEdit,
    onH3CoreCreate,
    onJourneysCreate,
    isJourneysEnabled,
    hasJourneysAutomation,
    stage,
  } = props;
  const { t } = useStore();
  const { hasRolePermission } = usePermissions();
  const hasManageAccess = useManageJobAccess(stage.job.createdById?.id);
  const hasIntegrations =
    onHireflix ||
    onOnboardingForm ||
    onADManager ||
    onH3CoreEdit ||
    onH3CoreCreate ||
    onJourneysCreate;
  const isStageEmpty = stage.count === 0;

  return (
    <>
      <Menu.Label label={t('job.stages.menu.title')} />
      {hasRolePermission('job:rename') && (
        <Menu.Item onSelect={onEdit} icon={<IconEdit />} label={t('job.stages.menu.rename')} />
      )}
      {hasManageAccess && (
        <Menu.Item
          onSelect={onRemove}
          variant="danger"
          dataCy="remove-stage"
          isDisabled={stage.isEntryStage || !isStageEmpty}
          icon={<IconTrash />}
          label={t('job.stages.menu.remove')}
        />
      )}

      {!isStageEmpty && (
        <>
          <Menu.Item
            hideOnClick={false}
            onSelect={(e) => {
              e.stopPropagation();
              e.preventDefault();
              nextPage();
            }}
            icon={<IconSort />}
            label={t('job.stages.menu.sort')}
          />

          {hasRolePermission('applicants:message') && (
            <>
              <Menu.Separator />
              <Menu.Label label={t('job.stages.menu.applicants')} />
              <Menu.Item
                icon={<IconMessages />}
                onSelect={onMessage}
                label={t('job.stages.menu.message')}
              />
              <Menu.Item icon={<IconHire />} onSelect={onHire} label={t('job.stages.menu.hire')} />
              <Menu.Item
                icon={<IconReject />}
                variant="danger"
                onSelect={onReject}
                label={t('job.stages.menu.reject')}
              />
            </>
          )}
          {hasIntegrations && <Menu.Separator />}
          {onHireflix && (
            <Menu.Item
              icon={<IconBrandHireflix size={24} />}
              onSelect={onHireflix}
              label={t('integrations.hireflix.bulk.invite')}
            />
          )}
          {onADManager && (
            <Menu.Item
              icon={<IconBrandADManager size={24} />}
              onSelect={onADManager}
              label={t('integrations.admanager.bulk.send')}
            />
          )}
          {onOnboardingForm && (
            <Menu.Item
              icon={<IconOnboarding size={24} />}
              onSelect={onOnboardingForm}
              label={t('integrations.onboardingform.bulk.send')}
            />
          )}
          {onH3CoreEdit && (
            <Menu.Item
              icon={<IconBrandH3 size={24} />}
              onSelect={onH3CoreEdit}
              label={t('integrations.h3core.bulk.edit')}
            />
          )}
          {onH3CoreCreate && (
            <Menu.Item
              icon={<IconBrandH3 size={24} />}
              onSelect={onH3CoreCreate}
              label={t('integrations.h3core.bulk.create')}
            />
          )}
          {onJourneysCreate && (
            <Menu.Item
              icon={<IconBrandJourneys size={24} />}
              onSelect={onJourneysCreate}
              label={t('integrations.journeys.bulk.create')}
              isDisabled={!isJourneysEnabled}
            />
          )}
        </>
      )}
      {hasJourneysAutomation && <AddJourneyStageTriggerMenuItem stage={stage} />}
    </>
  );
});

/**
 * "Add Journey stage trigger" menu item, shown for stages without a stage
 * trigger. Lives in its own component so its hooks only run for companies
 * that pass the journeys automation gate, and only while the menu is open
 * (Menu mounts its children lazily).
 */
const AddJourneyStageTriggerMenuItem = observer(({ stage }) => {
  const { t } = useStore();
  const isAdmin = useIsAppsAdmin();
  const triggersQuery = useJobTriggers(stage.job.id);
  const { addStageTrigger } = useAddStageTrigger(stage.job.id);

  // A stage takes at most one stage trigger, so offering this item without a
  // loaded trigger list would show it on stages that already have one and the
  // create would come back 400. The chip bails out on the same states.
  if (!triggersQuery.isSuccess) {
    return null;
  }

  const stageHasTrigger = triggersQuery.data.some(
    (trigger) => trigger.triggerType === 'stage' && trigger.jobStage === stage.id
  );

  if (stageHasTrigger) {
    return null;
  }

  return (
    <>
      <Menu.Separator />
      <Menu.Item
        icon={<IconBrandJourneys size={24} />}
        onSelect={() => addStageTrigger(stage.id, { isAdmin })}
        dataCy="stage-action-add-journey-trigger"
        label={t('integrations.journeys.stage.addTrigger')}
      />
    </>
  );
});

const PageOne = observer(({ goBack, onSortClick, currentSort }) => {
  const { t } = useStore();
  return (
    <>
      <Menu.Item
        hideOnClick={false}
        icon={<IconArrowLeft />}
        onSelect={(e) => {
          e.stopPropagation();
          e.preventDefault();
          goBack();
        }}
        label={t('job.stages.menu.sort.title')}
      />
      <Menu.Separator />
      {sorting.map(({ text, key }) => (
        <Menu.Item
          active={currentSort === key}
          key={key}
          onSelect={() => onSortClick(key)}
          label={t(text)}
        />
      ))}
    </>
  );
});

const sorting = [
  { text: 'job.stages.menu.sort.name-asc', key: 'name-asc' },
  { text: 'job.stages.menu.sort.name-desc', key: 'name-desc' },
  { text: 'job.stages.menu.sort.newest', key: 'date-desc' },
  { text: 'job.stages.menu.sort.oldest', key: 'date-asc' },
  { text: 'job.stages.menu.sort.notes.highest', key: 'notes-desc' },
  { text: 'job.stages.menu.sort.notes.lowest', key: 'notes-asc' },
  { text: 'job.stages.menu.sort.avgvote.highest', key: 'avgvote-desc' },
  { text: 'job.stages.menu.sort.avgvote.lowest', key: 'avgvote-asc' },
  { text: 'job.stages.menu.sort.votecount.highest', key: 'votes-desc' },
  { text: 'job.stages.menu.sort.votecount.lowest', key: 'votes-asc' },
];
